import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Plus, Trash2 } from 'lucide-react';

/**
 * CRUD form for a service-template (used when generating proposals).
 * Presentational component — the parent owns `templateForm` state and
 * all phase/activity mutations, which keeps this modal free of API calls
 * and trivially composable.
 */
export default function TemplateCrudModal({
  open,
  onOpenChange,
  editingTemplate,
  templateForm,
  setTemplateForm,
  addPhase,
  updatePhase,
  removePhase,
  addActivity,
  updateActivity,
  removeActivity,
  onSubmit,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingTemplate ? 'Editar Template' : 'Novo Template de Serviço'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-6 mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Nome do Template *</Label>
              <Input
                value={templateForm.name}
                onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                placeholder="Ex: Licenciamento Ambiental"
                required
              />
            </div>
            <div>
              <Label>Descrição</Label>
              <Input
                value={templateForm.description}
                onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                placeholder="Breve descrição do serviço"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-gray-900">Fases</h4>
              <Button type="button" onClick={addPhase} variant="outline" size="sm">
                <Plus className="w-4 h-4 mr-1" />
                Adicionar Fase
              </Button>
            </div>

            {templateForm.phases.map((phase, phaseIdx) => (
              <Card key={phase._uid || `tmpl-phase-${phaseIdx}`} className="p-4 border border-gray-200">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex-1 grid md:grid-cols-3 gap-3">
                    <div className="md:col-span-2">
                      <Label>Nome da Fase</Label>
                      <Input
                        value={phase.name}
                        onChange={(e) => updatePhase(phaseIdx, 'name', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Preço Sugerido (R$)</Label>
                      <Input
                        type="number"
                        value={phase.suggested_price}
                        onChange={(e) => updatePhase(phaseIdx, 'suggested_price', parseFloat(e.target.value) || 0)}
                        step="0.01"
                      />
                    </div>
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={() => removePhase(phaseIdx)} className="text-red-600 mt-6">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Atividades Padrão</Label>
                    <Button type="button" variant="ghost" size="sm" onClick={() => addActivity(phaseIdx)}>
                      <Plus className="w-3 h-3 mr-1" />
                      Adicionar
                    </Button>
                  </div>
                  {phase.activities.map((activity, actIdx) => (
                    <div key={`${phase._uid || phaseIdx}-tmpl-act-${actIdx}`} className="flex items-center gap-2">
                      <Input
                        value={activity.name || ''}
                        onChange={(e) => updateActivity(phaseIdx, actIdx, e.target.value)}
                        placeholder="Descrição da atividade"
                        className="flex-1"
                      />
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeActivity(phaseIdx, actIdx)} className="text-red-600">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" className="bg-emerald-800 hover:bg-emerald-900">
              {editingTemplate ? 'Salvar Alterações' : 'Criar Template'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
